import type { AxiosInstance } from "axios";
import HttpFactory from "~/repository/factory";
import { ENDPOINTS } from "~/repository/endpoints";
import type {
  AttendanceAuditResponse,
  AttendanceCatalogResponse,
  AttendanceCalendarResponse,
  AttendanceCalendarSummaryFilters,
  AttendanceCalendarSummaryResponse,
  AttendanceClassroomRosterResponse,
  AttendanceListFilters,
  AttendanceMakeupCase,
  AttendanceMakeupDecisionInput,
  AttendanceMakeupFilters,
  AttendanceMakeupGroup,
  AttendanceMakeupListResponse,
  AttendanceMakeupSessionCancelInput,
  AttendanceMakeupSessionCompleteInput,
  AttendanceMakeupSessionCreateInput,
  AttendanceMakeupSessionPatchInput,
  AttendanceOverviewResponse,
  AttendanceRecord,
  AttendanceRecordPatchInput,
  AttendanceSessionCreateInput,
  AttendanceSessionDetail,
  AttendanceSessionRecordsInput,
  AttendanceSettings,
  AttendanceSettingsInput,
  AttendanceStudentResponse,
} from "~/types/attendance";

class AttendanceModule extends HttpFactory {
  constructor(axiosInstance: AxiosInstance) {
    super(axiosInstance, "teacher");
  }

  async getCatalog(signal?: AbortSignal): Promise<AttendanceCatalogResponse> {
    return await this.get(ENDPOINTS.ATTENDANCE.CATALOG, {}, { signal });
  }

  async getOverview(
    filters: AttendanceListFilters,
    signal?: AbortSignal,
  ): Promise<AttendanceOverviewResponse> {
    return await this.get(ENDPOINTS.ATTENDANCE.OVERVIEW, filters, { signal });
  }

  async exportOverview(filters: AttendanceListFilters): Promise<Blob> {
    const response = await this.getResponse<Blob>(
      ENDPOINTS.ATTENDANCE.OVERVIEW_EXPORT,
      filters,
      { responseType: "blob" },
    );
    return response.data;
  }

  async getCalendar(
    filters: AttendanceListFilters,
    signal?: AbortSignal,
  ): Promise<AttendanceCalendarResponse> {
    return await this.get(ENDPOINTS.ATTENDANCE.CALENDAR, filters, { signal });
  }

  async getCalendarSummary(
    filters: AttendanceCalendarSummaryFilters,
    signal?: AbortSignal,
  ): Promise<AttendanceCalendarSummaryResponse> {
    return await this.get(ENDPOINTS.ATTENDANCE.CALENDAR_SUMMARY, filters, {
      signal,
    });
  }

  async getClassroomRoster(
    classroomId: number,
  ): Promise<AttendanceClassroomRosterResponse> {
    return await this.get(
      `${ENDPOINTS.ATTENDANCE.CLASSROOMS}${classroomId}/roster/`,
    );
  }

  async createSession(
    input: AttendanceSessionCreateInput,
  ): Promise<AttendanceSessionDetail> {
    return await this.post(ENDPOINTS.ATTENDANCE.SESSIONS, input);
  }

  async getSession(sessionId: string): Promise<AttendanceSessionDetail> {
    return await this.get(`${ENDPOINTS.ATTENDANCE.SESSIONS}${sessionId}/`);
  }

  async replaceSessionRecords(
    sessionId: string,
    input: AttendanceSessionRecordsInput,
  ): Promise<AttendanceSessionDetail> {
    return await this.put(
      `${ENDPOINTS.ATTENDANCE.SESSIONS}${sessionId}/records/`,
      input,
    );
  }

  async updateRecord(
    recordId: string,
    input: AttendanceRecordPatchInput,
  ): Promise<AttendanceRecord> {
    return await this.patch(
      `${ENDPOINTS.ATTENDANCE.RECORDS}${recordId}/`,
      input,
    );
  }

  async getSessionAudit(
    sessionId: string,
    page = 1,
    pageSize = 20,
  ): Promise<AttendanceAuditResponse> {
    return await this.get(
      `${ENDPOINTS.ATTENDANCE.SESSIONS}${sessionId}/audit/`,
      { page, pageSize },
    );
  }

  async getStudent(
    studentId: number,
    filters: AttendanceListFilters,
    signal?: AbortSignal,
  ): Promise<AttendanceStudentResponse> {
    return await this.get(
      `${ENDPOINTS.ATTENDANCE.STUDENTS}${studentId}/`,
      filters,
      { signal },
    );
  }

  async getSettings(): Promise<AttendanceSettings> {
    return await this.get(ENDPOINTS.ATTENDANCE.SETTINGS);
  }

  async updateSettings(
    input: AttendanceSettingsInput,
  ): Promise<AttendanceSettings> {
    return await this.patch(ENDPOINTS.ATTENDANCE.SETTINGS, input);
  }

  async getMakeups(
    filters: AttendanceMakeupFilters = {},
    signal?: AbortSignal,
  ): Promise<AttendanceMakeupListResponse> {
    return await this.get(ENDPOINTS.ATTENDANCE.MAKEUPS, filters, { signal });
  }

  async decideMakeup(
    makeupCaseId: string,
    input: AttendanceMakeupDecisionInput,
  ): Promise<AttendanceMakeupCase> {
    return await this.patch(
      `${ENDPOINTS.ATTENDANCE.MAKEUPS}${makeupCaseId}/decision/`,
      input,
    );
  }

  async createMakeupSession(
    input: AttendanceMakeupSessionCreateInput,
  ): Promise<AttendanceMakeupGroup> {
    return await this.post(ENDPOINTS.ATTENDANCE.MAKEUP_SESSIONS, input);
  }

  async getMakeupSessionGroup(
    makeupSessionOrGroupId: string,
  ): Promise<AttendanceMakeupGroup> {
    return await this.get(
      `${ENDPOINTS.ATTENDANCE.MAKEUP_SESSIONS}${makeupSessionOrGroupId}/`,
    );
  }

  async updateMakeupSession(
    makeupSessionId: string,
    input: AttendanceMakeupSessionPatchInput,
  ): Promise<AttendanceMakeupGroup> {
    return await this.patch(
      `${ENDPOINTS.ATTENDANCE.MAKEUP_SESSIONS}${makeupSessionId}/`,
      input,
    );
  }

  async cancelMakeupSession(
    makeupSessionId: string,
    input: AttendanceMakeupSessionCancelInput,
  ): Promise<AttendanceMakeupGroup> {
    return await this.post(
      `${ENDPOINTS.ATTENDANCE.MAKEUP_SESSIONS}${makeupSessionId}/cancel/`,
      input,
    );
  }

  async completeMakeupSession(
    makeupSessionId: string,
    input: AttendanceMakeupSessionCompleteInput,
  ): Promise<AttendanceMakeupGroup> {
    return await this.post(
      `${ENDPOINTS.ATTENDANCE.MAKEUP_SESSIONS}${makeupSessionId}/complete/`,
      input,
    );
  }
}

export default AttendanceModule;
